/**
 * The most specific thing the server said about a failed request.
 *
 * Our API answers a rejection with an envelope whose top-level `message` is a
 * SUMMARY, not the reason:
 *
 * ```json
 * { "error": {
 *     "code": "VALIDATION_FAILED",
 *     "message": "Some fields need attention.",
 *     "issues": [
 *       { "path": ["customer","email"], "message": "Invalid email address" },
 *       { "path": ["items",0,"pax"],    "message": "Add at least one" }
 *     ] } }
 * ```
 *
 * The storefront was showing only `message`, so a traveler whose booking was
 * refused for six separate, named reasons read "Some fields need attention."
 * and had nothing to act on. The reasons were sitting in the same response.
 *
 * operator-web has solved this since its `describeApiError`; this is the same
 * rule for the customer side, and deliberately keeps its wording and its
 * three-item cap so the two apps explain a rejection the same way.
 *
 * `stack` is never read. It is present in development responses and is not
 * something to put in front of a customer.
 */

import type { Translator } from "@/lib/translation-context";

import {
  humanForCode,
  isMachinePhrasing,
  labelForPath,
  humanForTransportError,
  humanForStaleDeployment,
} from "@/lib/api-failure-copy";
import {
  type FailedResponse,
  isServerFailureStatus,
  serverFailureMessage,
} from "@/lib/server-failure";

export interface ApiIssue {
  path?: unknown[];
  code?: string;
  message?: string;
}

interface ApiFailureBody {
  error?: {
    code?: string;
    message?: string;
    /** traveler-api's envelope. */
    issues?: ApiIssue[];
    /** The Next route handlers' envelope — Zod's own `flatten()`. Same
     *  information, different shape, because these routes validate in-process
     *  instead of forwarding to traveler-api. Both are ours, so both are read
     *  here rather than making every caller know which endpoint it hit. */
    errors?: {
      formErrors?: unknown;
      fieldErrors?: Record<string, unknown>;
    };
  };
}

function flattenedMessages(body: ApiFailureBody | null): string[] {
  const errors = body?.error?.errors;
  if (!errors) return [];
  const form = Array.isArray(errors.formErrors) ? errors.formErrors : [];
  const fields = Object.values(errors.fieldErrors ?? {}).flatMap((v) =>
    Array.isArray(v) ? v : [],
  );
  return [...form, ...fields]
    .map((m) => (typeof m === "string" ? m.trim() : ""))
    .filter(Boolean);
}

/**
 * Summaries our API sends ALONGSIDE the real reasons. Showing one of these to
 * a traveler tells them nothing they did not already know from the fact that
 * it failed, so they never win over an issue list — and when they are all
 * there is, the caller's own fallback is at least written for the situation.
 */
const GENERIC_MESSAGES = new Set([
  "some fields need attention.",
  "some fields need attention",
  "validation failed",
  "bad request",
  "unprocessable entity",
  "internal server error",
  "request failed",
]);

/** How many reasons a banner shows before summarising the rest. Matches
 *  operator-web's `describeApiError`, so both apps explain a rejection the
 *  same way. */
const MAX_REASONS = 3;

function isGeneric(message: string): boolean {
  return GENERIC_MESSAGES.has(message.trim().toLowerCase());
}

function asBody(body: unknown): ApiFailureBody | null {
  return typeof body === "object" && body !== null
    ? (body as ApiFailureBody)
    : null;
}

/**
 * Every distinct reason the server listed, in order, already trimmed —
 * minus the ones phrased for whoever wrote the schema rather than for the
 * person filling the form. See `isMachinePhrasing`.
 */
export function apiFailureIssues(body: unknown): string[] {
  const parsed = asBody(body);
  const issues = parsed?.error?.issues;
  // `issue?.message?.trim()` guarded the property, not the CALL: a non-string
  // `message` throws `TypeError: … is not a function`, and in checkout that
  // lands in the outer handler, which paints the TypeError text in the banner.
  // Our APIs do not send that shape; a proxy or a future endpoint might.
  const fromIssues = Array.isArray(issues)
    ? issues
        .map((issue) => {
          const message =
            typeof issue?.message === "string" ? issue.message.trim() : "";
          if (!message) return "";
          // Judge the RAW message, before any label is attached. The
          // disclosure list ends in `^\S{16,}$` — "a single long token is an
          // identifier, never a sentence" — and prefixing "Email address — "
          // puts spaces in the string, so that rule stops matching and the
          // identifier reaches the page. Filtering after labelling defeats
          // the last line of the guard.
          if (isMachinePhrasing(message)) return "";
          // Name the field the reason is about. Two different problems often
          // arrive as the SAME sentence ("This field is required"), and the
          // de-duplication below would then collapse them into one line
          // naming nothing. Skipped when the message already says it, so
          // "Name is required" does not become "Name — Name is required".
          const label = labelForPath(issue?.path);
          if (!label) return message;
          return message.toLowerCase().startsWith(label.toLowerCase())
            ? message
            : `${label} — ${message}`;
        })
        .filter(Boolean)
    : [];
  const all = [...fromIssues, ...flattenedMessages(parsed)] as string[];
  return Array.from(new Set(all)).filter((m) => !isMachinePhrasing(m));
}

/**
 * One line a traveler can act on. Prefers the listed reasons, then a real
 * domain message ("This departure is sold out"), then the caller's fallback.
 *
 * Capped at three reasons so a form with a dozen problems does not fill the
 * page with a wall of red — the fields carry their own messages too.
 *
 * Pass the `response` whenever you have it: a 5xx then reads as one generic
 * sentence with a support reference instead of anything the server wrote
 * (see `server-failure.ts`). Codes `humanForCode` knows keep their own copy.
 */
export function describeApiFailure(
  body: unknown,
  fallback: string,
  t: Translator,
  response?: FailedResponse,
): string {
  // The code first. It is the one part of the envelope written to be matched
  // on, and it lets us say what happened in words a traveler uses instead of
  // the domain layer's own ("Concurrent modification detected; retry").
  const human = humanForCode(asBody(body)?.error?.code, t);
  if (human) return human;

  if (response && isServerFailureStatus(response.status))
    return serverFailureMessage(body, response, t);

  const issues = apiFailureIssues(body);
  if (issues.length > 0) {
    const shown = issues.slice(0, MAX_REASONS).join(" · ");
    const hidden = issues.length - MAX_REASONS;
    // Never truncate silently. A traveler who fixes the three named problems
    // and is refused again would have no way of knowing a fourth was there.
    return hidden > 0 ? `${shown} · and ${hidden} more` : shown;
  }

  // A domain message from a code with no entry in `humanForCode`. Readable
  // ones get through; the schema-shaped ones are filtered. This is a weaker
  // net than the code map above — it can only catch phrasing it recognises —
  // so a new refusal that reads badly is fixed by adding its code there, not
  // by widening the regex.
  const message = asBody(body)?.error?.message?.trim();
  if (message && !isGeneric(message) && !isMachinePhrasing(message))
    return message;

  return fallback;
}

/**
 * The same rule for a thrown `Error` rather than a parsed body — the shape the
 * cart's writes and the checkout's own throws arrive in. A generic message is
 * discarded in favour of the caller's fallback, which at least names what was
 * being attempted.
 */
export function describeThrown(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    const message = error.message.trim();
    // The browser's own wording for "this request never left the device".
    // It reads as ordinary English, so neither filter below stops it, and a
    // customer was shown "Failed to fetch" as the reason a cart change did
    // not save. Found in live QA, not by reading the code.
    const transport = humanForTransportError(message);
    if (transport) return transport;
    // A deployment newer than this page. Framework wording with a hash and a
    // nextjs.org link otherwise reaches the customer intact — it reads as
    // ordinary English, so neither filter below stops it.
    const stale = humanForStaleDeployment(message);
    if (stale) return stale;
    // Same bar as an issue: specific is not enough if it is not readable.
    if (
      message.length > 0 &&
      !isGeneric(message) &&
      !isMachinePhrasing(message)
    )
      return message;
  }
  return fallback;
}
